import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, ExternalLink, AlertTriangle, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { toast } from 'sonner';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { qboOAuthErrorMessage } from '../../lib/qbo-errors';
import { parseInstantDate } from '../../lib/calendar-date';
import { effectiveTimeZone } from '../../lib/tenant-zone';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';

/**
 * A connection or sync INSTANT on the tenant's clock (companies.time_zone),
 * in the viewer's own locale format as before — only the zone moved off the
 * device. An em dash for an unreadable instant or an unknown zone.
 */
function inTenantZone(value: string | null | undefined, timeZone: string | null, part: 'date' | 'both'): string {
  const at = parseInstantDate(value);
  if (!at || !timeZone) return '\u2014';
  try {
    return part === 'date'
      ? at.toLocaleDateString(undefined, { timeZone: effectiveTimeZone(timeZone, at) })
      : at.toLocaleString(undefined, { timeZone: effectiveTimeZone(timeZone, at) });
  } catch {
    return '\u2014'; // an unrecognised zone throws RangeError
  }
}

const inputCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm font-mono placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50';

interface IntegrationData {
  qbo_connected: boolean;
  qbo_last_sync?: string;
  d365_connected: boolean;
  d365_last_sync?: string;
  d365_company_name?: string;
  slack_webhook_url: string;
  slack_notifications_enabled: boolean;
  slack_last_test?: string;
}

function StatusDot({ connected, warn = false }: { connected: boolean; warn?: boolean }) {
  const color = warn ? 'bg-amber-400' : connected ? 'bg-green-400' : 'bg-fieldiq-muted';
  return (
    <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} />
  );
}

interface QboStatus {
  connected: boolean;
  realmId: string | null;
  connectedAt: string | null;
  tokenExpiresAt: string | null;
  /**
   * QuickBooks refused the saved grant (API 0493). The realm and mappings are
   * kept; pushes refuse with QBO_RECONNECT_REQUIRED until an admin reconnects.
   * Absent on an API older than 0493 — read as false.
   */
  reconnectRequired?: boolean;
  reconnectRequiredAt?: string | null;
}

export default function IntegrationsPage() {
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showQboDisconnect, setShowQboDisconnect] = useState(false);
  const [qboError, setQboError] = useState<string | null>(null);

  const { data, isLoading } = useQuery<IntegrationData>({
    queryKey: ['settings', 'integrations'],
    queryFn: () => apiClient.get('/settings/integrations').then(r => r.data?.data ?? r.data ?? {}),
  });

  const integrations: IntegrationData = data ?? {
    qbo_connected: false, d365_connected: false,
    slack_webhook_url: '', slack_notifications_enabled: true,
  };

  // QBO — dedicated status endpoint
  const { data: qboStatus, isLoading: qboStatusLoading } = useQuery<QboStatus>({
    queryKey: ['quickbooks', 'status'],
    queryFn: () => apiClient.get('/quickbooks/status').then(r => r.data?.data ?? { connected: false, realmId: null, connectedAt: null, tokenExpiresAt: null }),
  });

  const qboConnected = qboStatus?.connected ?? false;
  const qboReconnectRequired = qboStatus?.reconnectRequired === true;

  // Handle the ?qbo= query param on OAuth return: refresh on success, surface the
  // failure on error. Both outcomes strip the params so a reload cannot replay a
  // stale banner, and so a dismissed banner stays dismissed.
  useEffect(() => {
    const outcome = searchParams.get('qbo');
    if (outcome !== 'connected' && outcome !== 'error') return;

    if (outcome === 'connected') {
      queryClient.invalidateQueries({ queryKey: ['quickbooks', 'status'] });
      setQboError(null);
    } else {
      // `reason` is absent on the live redirect; the helper owns that case.
      setQboError(qboOAuthErrorMessage(searchParams.get('reason')));
    }

    searchParams.delete('qbo');
    searchParams.delete('reason');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, queryClient]);

  // Auto-refresh QBO status when user returns to tab (e.g. after OAuth in new tab)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['quickbooks', 'status'] });
        }, 3000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [queryClient]);

  // QBO connect — get auth URL and open in new tab
  const qboConnectMutation = useMutation({
    mutationFn: () => apiClient.get('/quickbooks/auth-url').then(r => r.data),
    onSuccess: (res) => {
      const url = res?.data?.url || res?.url;
      if (url) window.open(url, '_blank');
      else toast.error('No OAuth URL returned');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to get QuickBooks auth URL'),
  });

  // QBO disconnect
  const qboDisconnectMutation = useMutation({
    mutationFn: () => apiClient.post('/quickbooks/disconnect'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quickbooks', 'status'] });
      setShowQboDisconnect(false);
      toast.success('QuickBooks disconnected');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to disconnect');
      setShowQboDisconnect(false);
    },
  });

  // D365
  const d365ConnectMutation = useMutation({
    mutationFn: () => apiClient.post('/integrations/d365/connect'),
    onSuccess: (res) => {
      const url = res.data?.data?.url || res.data?.url;
      if (url) window.location.href = url;
      else toast.error('No OAuth URL returned');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to connect D365'),
  });

  const d365DisconnectMutation = useMutation({
    mutationFn: () => apiClient.post('/integrations/d365/disconnect'),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['settings', 'integrations'] }); toast.success('Dynamics 365 disconnected'); },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to disconnect'),
  });

  // Slack
  const [slackUrl, setSlackUrl] = useState('');
  const [slackEnabled, setSlackEnabled] = useState(true);
  const [slackDirty, setSlackDirty] = useState(false);

  // Sync slack state from server
  useState(() => {
    if (data) {
      setSlackUrl(data.slack_webhook_url || '');
      setSlackEnabled(data.slack_notifications_enabled ?? true);
    }
  });

  // Update local slack state when data loads
  const prevData = data;
  if (prevData && !slackDirty && slackUrl !== (prevData.slack_webhook_url || '')) {
    setSlackUrl(prevData.slack_webhook_url || '');
    setSlackEnabled(prevData.slack_notifications_enabled ?? true);
  }

  const slackTestMutation = useMutation({
    mutationFn: () => apiClient.post('/integrations/slack/test'),
    onSuccess: () => toast.success('Test message sent to Slack'),
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Slack test failed'),
  });

  const slackSaveMutation = useMutation({
    mutationFn: (payload: { slack_webhook_url: string; slack_notifications_enabled: boolean }) =>
      apiClient.patch('/settings/integrations', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'integrations'] });
      setSlackDirty(false);
      toast.success('Slack settings saved');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to save'),
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" /></div>;
  }

  /* NO HEADER AND NO BACK ARROW HERE. The workspace above owns the h1 and the
     tab strip names this tab; the surface's band owns the way out. `navigate`
     stays — it still opens the QuickBooks mapping drill-in below. */
  return (
    <div className="min-h-full px-6 py-4 space-y-4">
        {qboError && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3"
          >
            <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-300">QuickBooks connection failed</p>
              <p className="text-xs text-red-200/80 mt-1">{qboError}</p>
            </div>
            <button
              type="button"
              onClick={() => setQboError(null)}
              aria-label="Dismiss QuickBooks connection error"
              className="shrink-0 rounded p-1 text-red-300/70 hover:text-red-200 hover:bg-red-500/20 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {qboReconnectRequired && (
          <div
            role="alert"
            data-testid="qbo-reconnect-required"
            className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3"
          >
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-300">QuickBooks needs to be reconnected</p>
              <p className="text-xs text-amber-200/80 mt-1">
                QuickBooks refused FieldIQ's saved sign-in
                {qboStatus?.reconnectRequiredAt ? ` on ${inTenantZone(qboStatus.reconnectRequiredAt, timeZone, 'both')}` : ''}.
                Invoices can't be pushed until you reconnect. Your QuickBooks company and its
                mappings are kept — reconnect to the same QuickBooks company.
              </p>
            </div>
          </div>
        )}

        {/* QuickBooks Online */}
        <div data-testid="qbo-card" className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-5">
          {qboStatusLoading ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-600/20 flex items-center justify-center">
                <Loader2 size={16} className="text-green-400 animate-spin" />
              </div>
              <div className="space-y-2">
                <div className="h-4 w-36 bg-fieldiq-border rounded animate-pulse" />
                <div className="h-3 w-52 bg-fieldiq-border rounded animate-pulse" />
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-600/20 flex items-center justify-center text-green-400 font-bold text-sm">QB</div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-white">QuickBooks Online</p>
                    <StatusDot connected={qboConnected} warn={qboReconnectRequired} />
                    <span className="text-xs text-fieldiq-muted">
                      {qboReconnectRequired ? 'Needs reconnect' : qboConnected ? 'Connected' : 'Not connected'}
                    </span>
                  </div>
                  <p className="text-xs text-fieldiq-muted mt-0.5">Direct API — invoice and payment sync</p>
                  {qboConnected && qboStatus?.connectedAt && (
                    <p className="text-[10px] text-fieldiq-muted mt-1">Connected {inTenantZone(qboStatus.connectedAt, timeZone, 'date')}</p>
                  )}
                </div>
              </div>
              {qboConnected ? (
                <div className="flex items-center gap-2">
                  {/* A route with no nav entry is invisible. This is the way in:
                      the mapping screen is only meaningful once connected, and
                      a push refuses without it. */}
                  <button onClick={() => navigate('/settings/integrations/quickbooks-mapping')}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-fieldiq-card border border-fieldiq-border text-white hover:border-fieldiq-accent">
                    Mapping
                  </button>
                  <button onClick={() => setShowQboDisconnect(true)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600/20 text-red-400 hover:bg-red-600/30">
                    Disconnect
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {/* Flagged connections keep their realm and mappings, so the
                      mapping screen stays reachable while reconnecting. */}
                  {qboReconnectRequired && (
                    <button onClick={() => navigate('/settings/integrations/quickbooks-mapping')}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-fieldiq-card border border-fieldiq-border text-white hover:border-fieldiq-accent">
                      Mapping
                    </button>
                  )}
                  <button onClick={() => qboConnectMutation.mutate()} disabled={qboConnectMutation.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90">
                    {qboConnectMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <ExternalLink size={12} />}
                    {qboReconnectRequired ? 'Reconnect' : 'Connect'}
                  </button>
                  {qboReconnectRequired && (
                    <button onClick={() => setShowQboDisconnect(true)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600/20 text-red-400 hover:bg-red-600/30">
                      Disconnect
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <ConfirmModal
          isOpen={showQboDisconnect}
          title="Disconnect QuickBooks Online"
          body="Are you sure? This will stop invoice syncing."
          confirmLabel="Disconnect"
          confirmVariant="destructive"
          loading={qboDisconnectMutation.isPending}
          onConfirm={() => qboDisconnectMutation.mutate()}
          onCancel={() => setShowQboDisconnect(false)}
        />

        {/* Microsoft Dynamics 365 */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-5">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center text-blue-400 font-bold text-sm">D365</div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-white">Microsoft Dynamics 365</p>
                  <StatusDot connected={integrations.d365_connected} />
                  <span className="text-xs text-fieldiq-muted">{integrations.d365_connected ? 'Connected' : 'Not connected'}</span>
                </div>
                <p className="text-xs text-fieldiq-muted mt-0.5">Business Central — invoice sync</p>
                {integrations.d365_company_name && (
                  <p className="text-xs text-fieldiq-accent mt-0.5">{integrations.d365_company_name}</p>
                )}
                {integrations.d365_last_sync && (
                  <p className="text-[10px] text-fieldiq-muted mt-1">Last sync: {inTenantZone(integrations.d365_last_sync, timeZone, 'both')}</p>
                )}
              </div>
            </div>
            {integrations.d365_connected ? (
              <button onClick={() => { if (window.confirm('Disconnect Dynamics 365?')) d365DisconnectMutation.mutate(); }}
                disabled={d365DisconnectMutation.isPending}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600/20 text-red-400 hover:bg-red-600/30">
                {d365DisconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
              </button>
            ) : (
              <button onClick={() => d365ConnectMutation.mutate()} disabled={d365ConnectMutation.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90">
                {d365ConnectMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <ExternalLink size={12} />}
                Connect
              </button>
            )}
          </div>
        </div>

        {/* Sage Intacct */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-5">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-600/20 flex items-center justify-center text-emerald-400 font-bold text-[10px]">SAGE</div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-white">Sage Intacct</p>
                  <StatusDot connected={(integrations as any).sage_connected ?? false} />
                  <span className="text-xs text-fieldiq-muted">{(integrations as any).sage_connected ? 'Connected' : 'Not connected'}</span>
                </div>
                <p className="text-xs text-fieldiq-muted mt-0.5">Cloud ERP — GL, AP, and invoice sync</p>
                {(integrations as any).sage_last_sync && (
                  <p className="text-[10px] text-fieldiq-muted mt-1">Last sync: {inTenantZone((integrations as any).sage_last_sync, timeZone, 'both')}</p>
                )}
              </div>
            </div>
            <button onClick={() => toast.info('Sage Intacct integration coming soon. Contact support to enable.')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90">
              <ExternalLink size={12} />
              Connect
            </button>
          </div>
        </div>

        {/* Slack */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center text-purple-400 font-bold text-sm">#</div>
            <div>
              <p className="text-sm font-semibold text-white">Slack</p>
              <p className="text-xs text-fieldiq-muted mt-0.5">Webhook-based notifications for dispatches, approvals, and alerts</p>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-white block mb-1">Webhook URL</label>
            <input className={inputCls} value={slackUrl}
              onChange={e => { setSlackUrl(e.target.value); setSlackDirty(true); }}
              placeholder="https://hooks.slack.com/services/T.../B.../..." />
            <p className="text-xs text-fieldiq-muted mt-1">Create an Incoming Webhook in your Slack workspace settings</p>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-white">Enable Slack notifications</p>
            <button onClick={() => { setSlackEnabled(!slackEnabled); setSlackDirty(true); }}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors shrink-0 ${slackEnabled ? 'bg-fieldiq-accent' : 'bg-fieldiq-border'}`}>
              <span className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${slackEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={() => slackTestMutation.mutate()} disabled={!slackUrl || slackTestMutation.isPending}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-600/20 text-purple-400 hover:bg-purple-600/30 disabled:opacity-50 disabled:cursor-not-allowed">
              {slackTestMutation.isPending ? 'Sending...' : 'Test Webhook'}
            </button>
            <button onClick={() => slackSaveMutation.mutate({ slack_webhook_url: slackUrl, slack_notifications_enabled: slackEnabled })}
              disabled={!slackDirty || slackSaveMutation.isPending}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                slackDirty ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
              }`}>
              {slackSaveMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : null}
              Save Slack Settings
            </button>
          </div>
      </div>
    </div>
  );
}
